import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">抱歉，找不到该漫画信息</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.genres.some((g) => comic.genres.includes(g))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${comic.category}推荐 | 香蕉漫画`,
    description: comic.description,
    image: comic.coverImage,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bananamanga.com/comic/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero / Breadcrumb */}
        <section className="bg-background-100 pt-20 pb-4">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.category, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </section>

        {/* Comic Detail */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Cover Image */}
              <div className="flex-shrink-0 mx-auto lg:mx-0">
                <div className="w-56 md:w-64 rounded-xl overflow-hidden border-2 border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - ${comic.category}推荐`}
                    className="w-full aspect-[2/3] object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap">
                    {comic.category}
                  </span>
                  {comic.genres.map((genre) => (
                    <span key={genre} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap">
                      {genre}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {comic.author}
                  </span>
                  <span className={`flex items-center gap-1 ${comic.status === '连载中' ? 'text-accent-600' : 'text-foreground-500'}`}>
                    <i className={comic.status === '连载中' ? 'ri-loader-4-line' : 'ri-check-double-line'}></i>
                    {comic.status} · {comic.totalChapters}话
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {(comic.views / 10000).toFixed(0)}万次浏览
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`${star <= Math.floor(comic.rating / 2) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-2xl font-bold text-primary-600 ml-1">{comic.rating}</span>
                  <span className="text-sm text-foreground-500">/ 10</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="mt-6 p-4 bg-background-50 rounded-xl border border-background-200/70">
                  <h2 className="font-heading font-semibold text-base text-foreground-950 mb-2">内容简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.fullDescription}</p>
                </div>

                {/* Action Button */}
                <div className="mt-6">
                  <Link
                    to={`/category/${comic.category}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    浏览更多{comic.category}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}